import { useMemo, useState, type ChangeEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, listResource } from '@/lib/api';
import { hierarchyLevelLabel, roleHierarchyLevel, roleLabel, sortRolesByHierarchy } from '@/lib/role-display';
import { compareCustomFieldOrder, customFieldFormSpan, customFieldUsesFullWidth } from '@/lib/custom-field-form-order';
import {
  fallbackUserFormLayout,
  formColumnSpan,
  formGroupIcon,
  isUserFormBuiltin,
  layoutGroupsForExtra,
  layoutItemsForSection,
  type UserFormLayoutItem,
  type UserFormSectionId,
} from '@/lib/user-form-layout';
import { userFormSuccessToast } from '@/lib/user-feedback';
import { CustomFieldPreviewField } from './CustomFieldPreviewField';

interface PreviewField {
  id: string;
  name: string;
  slug: string;
  type: string;
  placeholder?: string | null;
  helpText?: string | null;
  required?: boolean;
  options?: Array<{ label: string; value: string; isActive?: boolean }>;
  maxFiles?: number;
  optionsLayout?: 'vertical' | 'horizontal';
  formWidth?: 'half' | 'full' | null;
  sortOrder?: number;
  groupName?: string;
}

interface UserCustomFieldsFormData {
  personalFields: PreviewField[];
  sections: { groupName: string; fields: PreviewField[] }[];
  layout?: UserFormLayoutItem[];
}

interface RoleOption {
  id: string;
  name: string;
  label?: string | null;
  hierarchyLevel?: number;
}

const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const LABEL = 'text-sm font-medium text-dash-on-surface-variant';

function SectionHeading({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="mb-6 flex items-center gap-2">
      <span className="material-symbols-outlined text-dash-primary" aria-hidden="true">
        {icon}
      </span>
      <h2 className="text-xl font-semibold text-dash-on-surface">{title}</h2>
    </div>
  );
}

export function UserCustomFieldsPreviewPage() {
  const { toast } = useToast();
  const [formKey, setFormKey] = useState(0);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFileName, setAvatarFileName] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-custom-fields', 'form'],
    queryFn: () => api<UserCustomFieldsFormData>('/user-custom-fields/form'),
  });

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => listResource<RoleOption>('/roles'),
  });

  const personalFields = useMemo(
    () => [...(data?.personalFields ?? [])].sort(compareCustomFieldOrder),
    [data?.personalFields],
  );
  const extraFields = useMemo(
    () =>
      (data?.sections ?? [])
        .flatMap((section) =>
          section.fields.map((field) => ({ ...field, groupName: field.groupName || section.groupName })),
        )
        .sort(compareCustomFieldOrder),
    [data?.sections],
  );
  const roles = useMemo(
    () => sortRolesByHierarchy(rolesData?.data ?? []),
    [rolesData?.data],
  );
  const fieldById = useMemo(() => new Map([...personalFields, ...extraFields].map((field) => [field.id, field])), [personalFields, extraFields]);
  const layoutItems = useMemo(() => {
    if (data?.layout?.length) return data.layout;
    return fallbackUserFormLayout(
      personalFields.map((field) => ({ id: field.id, formWidth: customFieldUsesFullWidth(field) ? 'full' : 'half' })),
      extraFields.map((field) => ({ id: field.id, formWidth: customFieldUsesFullWidth(field) ? 'full' : 'half' })),
    );
  }, [data?.layout, personalFields, extraFields]);
  const extraGroups = useMemo(
    () =>
      layoutGroupsForExtra(
        layoutItems,
        (id) => fieldById.get(id)?.groupName,
        (data?.sections ?? []).map((section) => section.groupName),
      ),
    [layoutItems, fieldById, data?.sections],
  );

  function resetPreview() {
    setFormKey((key) => key + 1);
    setSelectedRoles([]);
    setAvatarPreview(null);
    setAvatarFileName('');
  }

  function handleAvatarChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(typeof ev.target?.result === 'string' ? ev.target.result : null);
    reader.readAsDataURL(file);
  }

  function renderPreviewField(item: UserFormLayoutItem, wide: 'md' | 'lg') {
    const field = fieldById.get(item.id);
    if (field) {
      return (
        <CustomFieldPreviewField
          key={item.id}
          field={field}
          className={customFieldFormSpan({ ...field, formWidth: item.formWidth }, wide)}
        />
      );
    }
    if (!isUserFormBuiltin(item.id)) return null;
    const span = formColumnSpan(item.formWidth, wide) ?? '';
    switch (item.id) {
      case 'firstName':
        return (
          <div key={item.id} className={`space-y-2 ${span}`}>
            <label htmlFor="preview-first-name" className={LABEL}>
              Nombre <span className="text-error-600">*</span>
            </label>
            <input id="preview-first-name" name="preview-first-name" type="text" required placeholder="Ej: Juan" className={FIELD} />
          </div>
        );
      case 'lastName':
        return (
          <div key={item.id} className={`space-y-2 ${span}`}>
            <label htmlFor="preview-last-name" className={LABEL}>
              Apellidos <span className="text-error-600">*</span>
            </label>
            <input id="preview-last-name" name="preview-last-name" type="text" required placeholder="Ej: Pérez" className={FIELD} />
          </div>
        );
      case 'email':
        return (
          <div key={item.id} className={`space-y-2 ${span}`}>
            <label htmlFor="preview-email" className={LABEL}>
              Correo electrónico <span className="text-error-600">*</span>
            </label>
            <input
              id="preview-email"
              name="preview-email"
              type="email"
              required
              placeholder="juan.perez@academia.edu"
              className={FIELD}
            />
          </div>
        );
      case 'phone':
        return (
          <div key={item.id} className={`space-y-2 ${span}`}>
            <label htmlFor="preview-phone" className={LABEL}>
              Teléfono
            </label>
            <input id="preview-phone" name="preview-phone" type="tel" placeholder="+56 9 1234 5678" className={FIELD} />
          </div>
        );
      case 'password':
        return (
          <div key={item.id} className={`space-y-2 ${span}`}>
            <label htmlFor="preview-password" className={LABEL}>
              Contraseña <span className="text-error-600">*</span>
            </label>
            <input id="preview-password" name="preview-password" type="password" required autoComplete="off" className={FIELD} />
          </div>
        );
      case 'passwordConfirmation':
        return (
          <div key={item.id} className={`space-y-2 ${span}`}>
            <label htmlFor="preview-password-confirmation" className={LABEL}>
              Confirmar contraseña <span className="text-error-600">*</span>
            </label>
            <input
              id="preview-password-confirmation"
              name="preview-password-confirmation"
              type="password"
              required
              autoComplete="off"
              className={FIELD}
            />
          </div>
        );
      case 'status':
        return (
          <div key={item.id} className={`space-y-2 ${span}`}>
            <label htmlFor="preview-status" className={LABEL}>
              Estado
            </label>
            <select id="preview-status" name="preview-status" defaultValue="active" className={FIELD}>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
        );
      case 'roles':
        return (
          <div key={item.id} className={`space-y-4 ${span}`}>
            <span className={LABEL}>
              Roles asignados <span className="text-error-600">*</span>
            </span>
            <div className="flex flex-wrap gap-3">
              {roles.map((role) => {
                const checked = selectedRoles.includes(role.name);
                return (
                  <label
                    key={role.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-full border border-dash-outline-variant px-4 py-2 transition-colors hover:bg-secondary-100 ${
                      checked ? 'border-dash-primary bg-brand-100 text-dash-primary' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() =>
                        setSelectedRoles((current) =>
                          current.includes(role.name) ? current.filter((name) => name !== role.name) : [...current, role.name],
                        )
                      }
                    />
                    <span className="text-sm font-medium">
                      {roleLabel(role.name, role.label)} ({hierarchyLevelLabel(role.hierarchyLevel ?? roleHierarchyLevel(role.name))})
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      default:
        return null;
    }
  }

  function renderPreviewSection(section: UserFormSectionId, icon: string, title: string, gridClass: string, wide: 'md' | 'lg') {
    const sectionItems = layoutItemsForSection(layoutItems, section);
    if (sectionItems.length === 0) return null;
    return (
      <section className="border-b border-dash-outline-variant p-6 md:p-8">
        <SectionHeading icon={icon} title={title} />
        <div className={gridClass}>{sectionItems.map((item) => renderPreviewField(item, wide))}</div>
      </section>
    );
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to="/campos-personalizados" label="Volver a campos del perfil de usuario" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Vista previa del formulario</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Puede completar el formulario. Al enviar se muestra el aviso de éxito, pero no se guarda nada.
          </p>
        </div>
      </div>

      {error ? (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <form
          key={formKey}
          className="lms-credentials-glass-panel overflow-hidden rounded-xl shadow-sm"
          onSubmit={(event) => {
            event.preventDefault();
            toast(userFormSuccessToast(false));
          }}
        >
          {renderPreviewSection('personal', 'person', 'Información Personal', 'grid grid-cols-1 gap-6 md:grid-cols-2', 'md')}
          {renderPreviewSection('security', 'lock', 'Seguridad', 'grid grid-cols-1 gap-6 md:grid-cols-2', 'md')}
          {renderPreviewSection(
            'account',
            'settings_account_box',
            'Configuración de Cuenta',
            'grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12',
            'lg',
          )}
          {extraGroups.map((group) => (
            <section key={group.groupName} className="border-b border-dash-outline-variant p-6 md:p-8">
              <SectionHeading icon={formGroupIcon(group.groupName)} title={group.groupName} />
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {group.items.map((item) => renderPreviewField(item, 'lg'))}
              </div>
            </section>
          ))}

          <section className="border-b border-dash-outline-variant p-6 md:p-8">
            <SectionHeading icon="add_a_photo" title="Foto de perfil" />
            <div className="flex flex-col items-center gap-8 md:flex-row">
              <div
                className={`flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-dash-outline-variant bg-secondary-100 ${
                  avatarPreview ? 'border-solid' : ''
                }`}
              >
                {avatarPreview ? (
                  <img src={avatarPreview} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="material-symbols-outlined text-4xl text-dash-on-surface-variant" aria-hidden="true">
                    account_circle
                  </span>
                )}
              </div>
              <div className="w-full flex-1">
                <label className="group flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-dash-outline-variant bg-secondary-50 p-8 text-center transition-colors hover:border-dash-primary">
                  <input type="file" accept="image/*" className="sr-only" onChange={handleAvatarChange} />
                  <span
                    className="material-symbols-outlined mb-2 text-3xl text-dash-primary transition-transform group-hover:scale-110"
                    aria-hidden="true"
                  >
                    cloud_upload
                  </span>
                  <p className="mb-1 text-sm font-medium text-dash-on-surface">Haga clic para subir o arrastre y suelte</p>
                  <p className="text-sm text-dash-on-surface-variant">PNG, JPG o WEBP (máx. 2MB)</p>
                  {avatarFileName ? <p className="mt-2 text-xs text-dash-secondary">{avatarFileName}</p> : null}
                </label>
              </div>
            </div>
          </section>

          <footer className="flex flex-col justify-end gap-4 bg-secondary-50/80 p-6 sm:flex-row md:p-8">
            <button
              type="button"
              onClick={resetPreview}
              className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-8 py-3 text-sm font-medium text-dash-primary transition-colors hover:bg-secondary-100"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-lg bg-dash-primary px-8 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98]"
            >
              Crear usuario
            </button>
          </footer>
        </form>
      )}
    </div>
  );
}
